import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dropin from "braintree-web-drop-in";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import config from "../Config/config";

const PAGE_TITLE = "Delivery Scheduling";
const WINDOW_LABEL = "12:00 PM - 6:00 PM";
const PAYMENT_TOKEN_TIMEOUT_MS = 8000;
const LOCAL_PAYMENT_FALLBACK_MS = 4000;
const LOCAL_TEST_PAYMENT_EMAIL = "vrushab1998@gmail.com";
const ACCOUNT_MANAGER = {
  name: "Jeff Kaplan",
  email: "jeff@auctionfuel.com",
  phone: "(317) 798-4459",
};

const normalizeEmailParam = (v) =>
  String(v || "")
    .trim()
    .replace(/^['"]+|['"]+$/g, "")
    .toLowerCase();
const normalizeOrgParam = (v) =>
  String(v || "")
    .trim()
    .replace(/^['"]+|['"]+$/g, "")
    .replace(/[^a-zA-Z0-9_-]/g, "");
const normalizeRegistrationParam = (v) =>
  String(v || "")
    .trim()
    .replace(/^['"]+|['"]+$/g, "");

const buildGoogleMapsUrl = (address = "") => {
  const safeAddress = String(address || "").trim();
  if (!safeAddress) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(safeAddress)}`;
};

const buildAppleMapsUrl = (address = "") => {
  const safeAddress = String(address || "").trim();
  if (!safeAddress) return "";
  return `https://maps.apple.com/?q=${encodeURIComponent(safeAddress)}`;
};

const fmtDate = (ms) => {
  if (!ms || !Number.isFinite(Number(ms))) return "—";
  try {
    return new Date(Number(ms)).toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const fmtDateTime = (ms) => {
  if (!ms || !Number.isFinite(Number(ms))) return "—";
  try {
    return new Date(Number(ms)).toLocaleString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const fmtCurrency = (amount) => {
  const safeAmount = Number(amount || 0);
  return Number.isFinite(safeAmount)
    ? safeAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })
    : "$0.00";
};

const mobileChipSx = {
  borderRadius: 999,
  fontWeight: 800,
  maxWidth: "100%",
  height: "auto",
  "& .MuiChip-label": {
    display: "block",
    whiteSpace: "normal",
    lineHeight: 1.2,
    paddingTop: "6px",
    paddingBottom: "6px",
  },
};

const mapIconButtonSx = {
  width: 38,
  height: 38,
  minWidth: 38,
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  color: "#fff",
  boxShadow: "0 10px 24px rgba(15,23,42,0.10)",
};

const stepCardStyle = (active) => ({
  borderRadius: 2.5,
  border: active ? "1px solid rgba(37,99,235,0.24)" : "1px solid rgba(148,163,184,0.18)",
  background: active ? "linear-gradient(180deg,#eff6ff 0%,#ffffff 100%)" : "#fff",
  boxShadow: active ? "0 18px 40px rgba(37,99,235,0.08)" : "none",
});

export default function DeliverySchedulingPage() {
  const [email, setEmail] = useState("");
  const [orgId, setOrgId] = useState("");
  const [orgName, setOrgName] = useState("");
  const [registrationId, setRegistrationId] = useState("");
  const [auctionId, setAuctionId] = useState("");
  const [auctionName, setAuctionName] = useState("");
  const [bidderName, setBidderName] = useState("");
  const [username, setUsername] = useState("");
  const [locationOptions, setLocationOptions] = useState([]);
  const [dateOptions, setDateOptions] = useState([]);
  const [locationMetadataRows, setLocationMetadataRows] = useState([]);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [delivery, setDelivery] = useState({});
  const [form, setForm] = useState({
    location: "",
    deliveryDateKey: "",
    address: "",
    phone: "",
  });
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [clientToken, setClientToken] = useState("");
  const [loadingToken, setLoadingToken] = useState(false);
  const [paymentFallbackMode, setPaymentFallbackMode] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const dropinInstance = useRef(null);
  const isLocalApi = useMemo(
    () => /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(String(config.API_BASE_URL || "").trim()),
    []
  );
  const localStorageEmail = useMemo(
    () => String(window.localStorage.getItem("email") || "").trim().toLowerCase(),
    []
  );
  const canUseLocalTestPayment = isLocalApi && localStorageEmail === LOCAL_TEST_PAYMENT_EMAIL;

  const isScheduled = delivery?.isScheduled === true;
  const selectedDateOption = useMemo(
    () => dateOptions.find((option) => option.dateKey === form.deliveryDateKey) || null,
    [dateOptions, form.deliveryDateKey]
  );
  const selectedLocationAddress = useMemo(() => {
    const match = locationMetadataRows.find((row) => row.name === form.location);
    return String(match?.address || "").trim();
  }, [form.location, locationMetadataRows]);
  const selectedLocationGoogleMapsUrl = useMemo(
    () => buildGoogleMapsUrl(selectedLocationAddress),
    [selectedLocationAddress]
  );
  const selectedLocationAppleMapsUrl = useMemo(
    () => buildAppleMapsUrl(selectedLocationAddress),
    [selectedLocationAddress]
  );
  const formComplete = Boolean(
    String(form.location || "").trim() &&
    String(form.deliveryDateKey || "").trim() &&
    String(form.address || "").trim() &&
    String(form.phone || "").trim()
  );

  const applyStatusData = useCallback(
    (
      data,
      {
        org = "",
        emailValue = "",
        registrationIdValue = "",
        auctionIdValue = "",
        fallbackLocationOptions = [],
        fallbackDateOptions = [],
        fallbackLocationMetadataRows = [],
        fallbackDeliveryFee = 0,
      } = {}
    ) => {
      const nextDelivery = data?.delivery || {};
      const nextLocationOptions = Array.isArray(data?.deliveryLocationOptions)
        ? data.deliveryLocationOptions
        : fallbackLocationOptions;
      const nextDateOptions = Array.isArray(data?.deliveryDateOptions)
        ? data.deliveryDateOptions
        : fallbackDateOptions;
      const nextLocationMetadataRows = Array.isArray(data?.deliveryLocationMetadataRows)
        ? data.deliveryLocationMetadataRows
        : fallbackLocationMetadataRows;
      const nextAddress = String(data?.address || nextDelivery?.address || "").trim();
      const nextPhone = String(data?.phone || nextDelivery?.phone || "").trim();
      const nextLocation =
        String(nextDelivery?.location || "").trim() ||
        String(nextLocationOptions[0] || "").trim();
      const nextDateKey =
        String(nextDelivery?.deliveryDateKey || "").trim() ||
        String(nextDateOptions[0]?.dateKey || "").trim();
      const nextDeliveryFee = Number(data?.deliveryFee ?? fallbackDeliveryFee);

      setExists(!!data?.exists);
      setBidderName(String(data?.bidderName || "").trim());
      setUsername(String(data?.username || "").trim());
      setAuctionName(String(data?.auctionName || "").trim());
      setAuctionId(String(data?.auctionId || auctionIdValue || "").trim());
      setRegistrationId(String(data?.registrationId || registrationIdValue || "").trim());
      setEmail(String(data?.email || emailValue || "").trim().toLowerCase());
      setOrgId(String(data?.publicOrgId || org || "").trim());
      setOrgName(String(data?.orgName || "").trim());
      setLocationOptions(nextLocationOptions);
      setDateOptions(nextDateOptions);
      setLocationMetadataRows(nextLocationMetadataRows);
      setDeliveryFee(Number.isFinite(nextDeliveryFee) ? nextDeliveryFee : 0);
      setDelivery(nextDelivery);
      setForm({
        location: nextLocation,
        deliveryDateKey: nextDateKey,
        address: nextAddress,
        phone: nextPhone,
      });
    },
    []
  );

  const loadStatus = useCallback(
    async ({
      org,
      email: emailValue,
      registrationId: registrationIdValue = "",
      auctionId: auctionIdValue = "",
    }) => {
      setLoading(true);
      setError("");
      setSuccess("");
      try {
        const { data } = await axios.get(`${config.API_BASE_URL}/delivery-scheduling/status`, {
          params: {
            org,
            ...(emailValue ? { email: emailValue } : {}),
            ...(registrationIdValue ? { registrationId: registrationIdValue } : {}),
            ...(auctionIdValue ? { auctionId: auctionIdValue } : {}),
          },
        });
        if (!data?.ok) throw new Error(data?.error || "Failed to load delivery scheduling status.");
        applyStatusData(data, {
          org,
          emailValue,
          registrationIdValue,
          auctionIdValue,
        });
        if (!data?.exists) {
          setError("No registration was found for this delivery link.");
        }
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || "Failed to load delivery scheduling status.");
      } finally {
        setLoading(false);
      }
    },
    [applyStatusData]
  );

  const teardownDropIn = () => {
    if (!dropinInstance.current) return;
    void dropinInstance.current.teardown();
    dropinInstance.current = null;
  };

  const resetPaymentState = ({ useFallback = false } = {}) => {
    teardownDropIn();
    setClientToken("");
    setLoadingToken(false);
    setPaymentFallbackMode(useFallback);
    setError("");
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailFromUrl = normalizeEmailParam(params.get("email"));
    const orgFromUrl = normalizeOrgParam(params.get("org") || params.get("orgId") || params.get("publicOrgId"));
    const registrationFromUrl = normalizeRegistrationParam(params.get("registrationId") || params.get("regId"));
    const auctionFromUrl = String(params.get("auctionId") || "").trim();

    if (!orgFromUrl) {
      setError("Missing organization key in link. Use ?org=<key>&registrationId=<docId>.");
      setLoading(false);
      return;
    }
    if (!registrationFromUrl && (!auctionFromUrl || !emailFromUrl)) {
      setError("Missing registrationId in link (or provide auctionId + email).");
      setLoading(false);
      return;
    }
    if (!registrationFromUrl && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailFromUrl)) {
      setError("Invalid email in link.");
      setLoading(false);
      return;
    }

    setOrgId(orgFromUrl);
    setEmail(emailFromUrl);
    setRegistrationId(registrationFromUrl);
    setAuctionId(auctionFromUrl);
    void loadStatus({
      org: orgFromUrl,
      email: emailFromUrl,
      registrationId: registrationFromUrl,
      auctionId: auctionFromUrl,
    });
  }, [loadStatus]);

  useEffect(() => {
    if (step !== 3 || isScheduled || clientToken || paymentFallbackMode) return;
    let cancelled = false;
    let fallbackTimer = null;

    const clearFallbackTimer = () => {
      if (fallbackTimer) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
    };

    const generateClientToken = async () => {
      setLoadingToken(true);

      if (canUseLocalTestPayment) {
        fallbackTimer = window.setTimeout(() => {
          if (cancelled) return;
          clearFallbackTimer();
          setClientToken("");
          setLoadingToken(false);
          setPaymentFallbackMode(true);
          setError("");
        }, LOCAL_PAYMENT_FALLBACK_MS);
      }

      try {
        const response = await axios.get(`${config.API_BASE_URL}/token`, {
          timeout: PAYMENT_TOKEN_TIMEOUT_MS,
        });
        if (cancelled) return;

        const nextClientToken = String(response?.data?.clientToken || "");
        clearFallbackTimer();

        if (nextClientToken) {
          setClientToken(nextClientToken);
          return;
        }

        setClientToken("");
        if (canUseLocalTestPayment) {
          setPaymentFallbackMode(true);
          setError("");
        } else {
          setError("Failed to initialize payment gateway.");
        }
      } catch (err) {
        if (cancelled) return;
        clearFallbackTimer();
        setClientToken("");
        if (canUseLocalTestPayment) {
          setPaymentFallbackMode(true);
          setError("");
        } else {
          setError("Failed to initialize payment gateway.");
        }
      } finally {
        if (!cancelled) {
          clearFallbackTimer();
          setLoadingToken(false);
        }
      }
    };

    void generateClientToken();
    return () => {
      cancelled = true;
      clearFallbackTimer();
    };
  }, [canUseLocalTestPayment, clientToken, isScheduled, paymentFallbackMode, step]);

  useEffect(() => {
    if (step !== 3 || isScheduled || paymentFallbackMode || !clientToken) return undefined;

    let cancelled = false;
    dropin
      .create({
        authorization: clientToken,
        container: "#delivery-dropin-container",
      })
      .then((instance) => {
        if (cancelled) {
          void instance.teardown();
          return;
        }
        dropinInstance.current = instance;
      })
      .catch((err) => {
        setError(err?.message || "Failed to load payment gateway.");
        if (canUseLocalTestPayment) setPaymentFallbackMode(true);
      });

    return () => {
      cancelled = true;
      if (dropinInstance.current) {
        void dropinInstance.current.teardown();
        dropinInstance.current = null;
      }
    };
  }, [canUseLocalTestPayment, clientToken, isScheduled, paymentFallbackMode, step]);

  const handleFieldChange = (field) => (event) => {
    const nextValue = event?.target?.value ?? "";
    setForm((prev) => ({ ...prev, [field]: nextValue }));
    if (error) setError("");
    if (success) setSuccess("");
  };

  const handleContinueToReview = () => {
    if (!formComplete) {
      setError("Please confirm your phone number, address, location, and delivery date first.");
      return;
    }
    setError("");
    setStep(2);
  };

  const handleConfirmDelivery = async () => {
    if (!paymentFallbackMode && !dropinInstance.current) {
      setError("Payment form is still loading. Please wait a moment and try again.");
      return;
    }
    if (!formComplete) {
      setError("Please complete all required delivery fields before confirming.");
      setStep(1);
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const paymentMethod = paymentFallbackMode
        ? { nonce: "", deviceData: "" }
        : await dropinInstance.current.requestPaymentMethod();
      const payload = {
        org: orgId,
        email,
        registrationId: registrationId || "",
        auctionId: auctionId || "",
        location: form.location,
        deliveryDateKey: form.deliveryDateKey,
        address: String(form.address || "").trim(),
        phone: String(form.phone || "").trim(),
        paymentMethodNonce: paymentMethod?.nonce || "",
        deviceData: paymentMethod?.deviceData || "",
        simulatePayment: paymentFallbackMode,
      };
      const { data } = await axios.post(`${config.API_BASE_URL}/delivery-scheduling/submit`, payload);
      if (!data?.ok) throw new Error(data?.error || "Failed to confirm delivery.");
      applyStatusData(data, {
        org: orgId,
        emailValue: email,
        registrationIdValue: registrationId,
        auctionIdValue: auctionId,
        fallbackLocationOptions: locationOptions,
        fallbackDateOptions: dateOptions,
        fallbackLocationMetadataRows: locationMetadataRows,
        fallbackDeliveryFee: deliveryFee,
      });
      teardownDropIn();
      setPaymentFallbackMode(false);
      setSuccess("Your delivery has been confirmed and cannot be edited online.");
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to confirm delivery.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetryPaymentLoad = () => {
    resetPaymentState();
  };

  const handleUseLocalTestPayment = () => {
    if (!canUseLocalTestPayment) return;
    resetPaymentState({ useFallback: true });
  };

  const renderSnapshot = () => (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 2.25, p: 1.6, mb: 2, borderColor: "#d9e1f2", background: "#f8fbff" }}
    >
      <Typography sx={{ color: "#1e3a8a", fontWeight: 900, mb: 1.2, fontSize: 14, letterSpacing: 0.3 }}>
        Registration Snapshot
      </Typography>
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
          },
        }}
      >
        {[
          { label: "Bidder Name", value: bidderName || username || "—" },
          { label: "Auction", value: auctionName || "—" },
        ].map((row) => (
          <Box key={row.label}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, letterSpacing: 0.2 }}>
              {row.label}
            </Typography>
            <Typography sx={{ color: "#0f172a", fontWeight: 800 }}>{row.value}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );

  const renderReadOnlyDetails = () => {
    const safeLocation = String(delivery?.location || "").trim();
    const locationAddress =
      String(locationMetadataRows.find((row) => row.name === safeLocation)?.address || "").trim();
    const googleMapsUrl = buildGoogleMapsUrl(locationAddress);
    const appleMapsUrl = buildAppleMapsUrl(locationAddress);
    return (
      <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, border: "1px solid rgba(37,99,235,0.16)" }}>
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: 2,
            background: "rgba(220,252,231,0.85)",
            border: "1px solid rgba(22,163,74,0.16)",
          }}
        >
          This delivery has already been scheduled. It cannot be edited online.
        </Alert>

        <Box sx={{ display: "grid", gap: 2 }}>
          <Box
            sx={{
              p: 1.75,
              borderRadius: 2.5,
              background: "linear-gradient(180deg,#eff6ff 0%,#ffffff 100%)",
              border: "1px solid rgba(59,130,246,0.14)",
            }}
          >
            <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 21 }}>
              Delivery Details
            </Typography>
            <Box sx={{ mt: 1.15, display: "grid", gap: 0.6 }}>
              <Typography variant="body2" sx={{ color: "#334155" }}>
                <b>Delivery day:</b> {delivery?.deliveryDateLabel || fmtDate(delivery?.windowStartAtMs)}
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155" }}>
                <b>Delivery window:</b> {WINDOW_LABEL}
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155" }}>
                <b>Location:</b> {delivery?.location || "—"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155" }}>
                <b>Address:</b> {delivery?.address || "—"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155" }}>
                <b>Phone:</b> {delivery?.phone || "—"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155" }}>
                <b>Scheduled at:</b> {fmtDateTime(delivery?.scheduledAtMs)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              p: 1.75,
              borderRadius: 2.5,
              background: "#fff",
              border: "1px solid rgba(148,163,184,0.18)",
            }}
          >
            <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 18 }}>
              Transaction Details
            </Typography>
            <Box sx={{ mt: 1.05, display: "grid", gap: 0.55 }}>
              <Typography variant="body2" sx={{ color: "#334155" }}>
                <b>Amount:</b> {fmtCurrency(delivery?.amount || deliveryFee)}
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155" }}>
                <b>Transaction ID:</b> {delivery?.transactionId || "—"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155" }}>
                <b>Status:</b> {delivery?.transactionStatus || "—"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155" }}>
                <b>Card:</b> {delivery?.cardType ? `${delivery.cardType}${delivery?.last4 ? ` ending in ${delivery.last4}` : ""}` : "—"}
              </Typography>
            </Box>
          </Box>

          {locationAddress ? (
            <Box
              sx={{
                p: 1.75,
                borderRadius: 2.5,
                background: "#fff",
                border: "1px solid rgba(148,163,184,0.18)",
                display: "grid",
                gap: 0.8,
              }}
            >
              <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 18 }}>
                Auction Location
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155" }}>
                {locationAddress}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" sx={{ color: "#475569", fontWeight: 700 }}>
                  Opens maps app:
                </Typography>
                <Button
                  component="a"
                  href={googleMapsUrl || undefined}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open in Google Maps"
                  sx={{
                    ...mapIconButtonSx,
                    background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                    "&:hover": {
                      background: "linear-gradient(135deg,#1d4ed8,#0891b2)",
                    },
                  }}
                >
                  <GoogleIcon sx={{ fontSize: 20 }} />
                </Button>
                <Button
                  component="a"
                  href={appleMapsUrl || undefined}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open in Apple Maps"
                  sx={{
                    ...mapIconButtonSx,
                    background: "linear-gradient(135deg,#0f172a,#334155)",
                    "&:hover": {
                      background: "linear-gradient(135deg,#020617,#1e293b)",
                    },
                  }}
                >
                  <AppleIcon sx={{ fontSize: 20 }} />
                </Button>
              </Box>
            </Box>
          ) : null}

          <Box
            sx={{
              p: 1.75,
              borderRadius: 2.5,
              background: "linear-gradient(180deg,#ffffff 0%,#eff6ff 100%)",
              border: "1px solid rgba(59,130,246,0.16)",
            }}
          >
            <Chip
              label="Auction Manager"
              sx={{
                ...mobileChipSx,
                mb: 1.2,
                color: "#1d4ed8",
                backgroundColor: "rgba(59,130,246,0.12)",
                fontWeight: 900,
              }}
            />
            <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 20 }}>
              Contact Jeff if you have any questions.
            </Typography>
            <Typography variant="body2" sx={{ color: "#475569", mt: 0.65 }}>
              {ACCOUNT_MANAGER.name} • {ACCOUNT_MANAGER.email} • {ACCOUNT_MANAGER.phone}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderStepOne = () => (
    <Paper sx={{ ...stepCardStyle(step === 1), p: { xs: 2, md: 2.5 } }}>
      <Chip label="Step 1 of 3" sx={{ ...mobileChipSx, mb: 1.2, fontWeight: 900 }} />
      <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 22 }}>
        Confirm your delivery information
      </Typography>
      <Typography variant="body2" sx={{ color: "#475569", mt: 0.55, lineHeight: 1.7 }}>
        Choose the auction location and delivery day, then confirm the phone number and address Jeff should use.
      </Typography>

      <Box sx={{ mt: 1.75, display: "grid", gap: 1.25 }}>
        <TextField
          label="Phone Number"
          value={form.phone}
          onChange={handleFieldChange("phone")}
          fullWidth
        />
        <TextField
          label="Delivery Address"
          value={form.address}
          onChange={handleFieldChange("address")}
          fullWidth
          multiline
          minRows={3}
        />
        <FormControl fullWidth>
          <InputLabel id="delivery-location-label">Location</InputLabel>
          <Select
            labelId="delivery-location-label"
            label="Location"
            value={form.location}
            onChange={handleFieldChange("location")}
          >
            {locationOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="delivery-date-label">Delivery Date</InputLabel>
          <Select
            labelId="delivery-date-label"
            label="Delivery Date"
            value={form.deliveryDateKey}
            onChange={handleFieldChange("deliveryDateKey")}
          >
            {dateOptions.map((option) => (
              <MenuItem key={option.dateKey} value={option.dateKey}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selectedLocationAddress ? (
        <Box
          sx={{
            mt: 1.5,
            p: 1.4,
            borderRadius: 2.25,
            background: "#fff",
            border: "1px solid rgba(148,163,184,0.18)",
            display: "grid",
            gap: 0.65,
          }}
        >
          <Typography variant="body2" sx={{ color: "#334155" }}>
            <b>Location address:</b> {selectedLocationAddress}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ color: "#475569", fontWeight: 700 }}>
              Opens maps app:
            </Typography>
            <Button
              component="a"
              href={selectedLocationGoogleMapsUrl || undefined}
              target="_blank"
              rel="noreferrer"
              aria-label="Open in Google Maps"
              sx={{
                ...mapIconButtonSx,
                background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                "&:hover": {
                  background: "linear-gradient(135deg,#1d4ed8,#0891b2)",
                },
              }}
            >
              <GoogleIcon sx={{ fontSize: 20 }} />
            </Button>
            <Button
              component="a"
              href={selectedLocationAppleMapsUrl || undefined}
              target="_blank"
              rel="noreferrer"
              aria-label="Open in Apple Maps"
              sx={{
                ...mapIconButtonSx,
                background: "linear-gradient(135deg,#0f172a,#334155)",
                "&:hover": {
                  background: "linear-gradient(135deg,#020617,#1e293b)",
                },
              }}
            >
              <AppleIcon sx={{ fontSize: 20 }} />
            </Button>
          </Box>
        </Box>
      ) : null}

      <Alert
        severity="info"
        sx={{
          mt: 1.5,
          borderRadius: 2,
          background: "rgba(239,246,255,0.88)",
          border: "1px solid rgba(59,130,246,0.16)",
        }}
      >
        Delivery will arrive on the selected day between {WINDOW_LABEL}.
      </Alert>

      <Box sx={{ mt: 1.75, display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Chip label={`Delivery Fee • ${fmtCurrency(deliveryFee)}`} sx={mobileChipSx} />
        {step === 1 ? (
          <Button
            onClick={handleContinueToReview}
            disabled={!formComplete}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 3,
              py: 1.05,
              fontWeight: 900,
              color: "#fff",
              background: formComplete
                ? "linear-gradient(90deg,#2563eb,#1d4ed8)"
                : "linear-gradient(90deg,#9ca3af,#6b7280)",
              "&:hover": {
                background: formComplete
                  ? "linear-gradient(90deg,#1d4ed8,#1e40af)"
                  : "linear-gradient(90deg,#9ca3af,#6b7280)",
              },
            }}
          >
            Continue to Review
          </Button>
        ) : null}
      </Box>
    </Paper>
  );

  const renderStepTwo = () => (
    <Paper sx={{ ...stepCardStyle(step === 2), p: { xs: 2, md: 2.5 } }}>
      <Chip label="Step 2 of 3" sx={{ ...mobileChipSx, mb: 1.2, fontWeight: 900 }} />
      <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 22 }}>
        Review your delivery selection
      </Typography>
      <Typography variant="body2" sx={{ color: "#475569", mt: 0.55, lineHeight: 1.7 }}>
        Double-check everything below before moving to payment.
      </Typography>

      <Alert
        severity="warning"
        sx={{
          mt: 1.5,
          borderRadius: 2,
          background: "rgba(254,249,195,0.92)",
          border: "1px solid rgba(202,138,4,0.18)",
        }}
      >
        This cannot be edited, so make sure you review the details before confirming delivery.
      </Alert>

      <Box
        sx={{
          mt: 1.5,
          p: 1.6,
          borderRadius: 2.25,
          background: "#fff",
          border: "1px solid rgba(148,163,184,0.18)",
          display: "grid",
          gap: 0.65,
        }}
      >
        <Typography variant="body2" sx={{ color: "#334155" }}>
          <b>Delivery location:</b> {form.location || "—"}
        </Typography>
        <Typography variant="body2" sx={{ color: "#334155" }}>
          <b>Delivery date:</b> {selectedDateOption?.label || "—"}
        </Typography>
        <Typography variant="body2" sx={{ color: "#334155" }}>
          <b>Delivery window:</b> {WINDOW_LABEL}
        </Typography>
        <Typography variant="body2" sx={{ color: "#334155" }}>
          <b>Phone:</b> {form.phone || "—"}
        </Typography>
        <Typography variant="body2" sx={{ color: "#334155" }}>
          <b>Address:</b> {form.address || "—"}
        </Typography>
        <Typography variant="body2" sx={{ color: "#334155" }}>
          <b>Delivery fee:</b> {fmtCurrency(deliveryFee)}
        </Typography>
      </Box>

      <Box sx={{ mt: 1.75, display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Button
          onClick={() => setStep(1)}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 2.6,
            py: 1,
            fontWeight: 800,
            color: "#334155",
            background: "rgba(226,232,240,0.78)",
            "&:hover": { background: "rgba(203,213,225,0.92)" },
          }}
        >
          Back
        </Button>
        {step === 2 ? (
          <Button
            onClick={() => setStep(3)}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 3,
              py: 1.05,
              fontWeight: 900,
              color: "#fff",
              background: "linear-gradient(90deg,#2563eb,#1d4ed8)",
              "&:hover": {
                background: "linear-gradient(90deg,#1d4ed8,#1e40af)",
              },
            }}
          >
            Continue to Payment
          </Button>
        ) : null}
      </Box>
    </Paper>
  );

  const renderStepThree = () => (
    <Paper sx={{ ...stepCardStyle(step === 3), p: { xs: 2, md: 2.5 } }}>
      <Chip label="Step 3 of 3" sx={{ ...mobileChipSx, mb: 1.2, fontWeight: 900 }} />
      <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 22 }}>
        Complete payment and confirm delivery
      </Typography>
      <Typography variant="body2" sx={{ color: "#475569", mt: 0.55, lineHeight: 1.7 }}>
        Once payment is submitted, your delivery selection will be locked and cannot be edited online.
      </Typography>

      <Box
        sx={{
          mt: 1.5,
          p: 1.6,
          borderRadius: 2.25,
          background: "#fff",
          border: "1px solid rgba(148,163,184,0.18)",
          display: "grid",
          gap: 0.65,
        }}
      >
        <Typography variant="body2" sx={{ color: "#334155" }}>
          <b>Delivery day:</b> {selectedDateOption?.label || "—"} between {WINDOW_LABEL}
        </Typography>
        <Typography variant="body2" sx={{ color: "#334155" }}>
          <b>Location:</b> {form.location || "—"}
        </Typography>
        <Typography variant="body2" sx={{ color: "#334155" }}>
          <b>Delivery fee:</b> {fmtCurrency(deliveryFee)}
        </Typography>
      </Box>

      <Box
        sx={{
          mt: 1.5,
          p: 1.6,
          borderRadius: 2.25,
          background: "#fff",
          border: "1px solid rgba(148,163,184,0.18)",
          minHeight: 180,
        }}
      >
        {paymentFallbackMode ? (
          <Box sx={{ py: 2, display: "grid", gap: 1.25 }}>
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                background: "rgba(220,252,231,0.88)",
                border: "1px solid rgba(22,163,74,0.16)",
              }}
            >
              Local test payment mode is active. This will save a simulated delivery payment so you can keep building the flow without Braintree.
            </Alert>
          </Box>
        ) : loadingToken && !clientToken ? (
          <Box sx={{ py: 5, display: "grid", placeItems: "center", gap: 1.25 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Loading payment form...
            </Typography>
            {canUseLocalTestPayment ? (
              <Typography variant="body2" sx={{ color: "#64748b", textAlign: "center", maxWidth: 420 }}>
                If Braintree does not respond within a few seconds, this page will switch to local test payment automatically.
              </Typography>
            ) : null}
          </Box>
        ) : !clientToken ? (
          <Box sx={{ py: 2, display: "grid", gap: 1.25 }}>
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Payment form did not load. This usually means the backend was not restarted yet, Braintree is not configured, or the request timed out.
            </Alert>
            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1, flexWrap: "wrap" }}>
              <Button
                onClick={handleRetryPaymentLoad}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 2.5,
                  py: 1,
                  fontWeight: 900,
                  color: "#fff",
                  background: "linear-gradient(90deg,#2563eb,#1d4ed8)",
                  "&:hover": {
                    background: "linear-gradient(90deg,#1d4ed8,#1e40af)",
                  },
                }}
              >
                Retry Payment Form
              </Button>
            </Box>
          </Box>
        ) : (
          <div id="delivery-dropin-container" />
        )}
      </Box>

      <Box sx={{ mt: 1.75, display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Button
          onClick={() => setStep(2)}
          disabled={submitting}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            px: 2.6,
            py: 1,
            fontWeight: 800,
            color: "#334155",
            background: "rgba(226,232,240,0.78)",
            "&:hover": { background: "rgba(203,213,225,0.92)" },
          }}
        >
          Back
        </Button>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {canUseLocalTestPayment ? (
            <Button
              onClick={handleUseLocalTestPayment}
              disabled={submitting || paymentFallbackMode}
              sx={{
                textTransform: "none",
                borderRadius: 999,
                px: 2.5,
                py: 1,
                fontWeight: 900,
                color: "#fff",
                background: "linear-gradient(90deg,#0f766e,#0d9488)",
                "&:hover": {
                  background: "linear-gradient(90deg,#115e59,#0f766e)",
                },
              }}
            >
              {paymentFallbackMode ? "Local Test Payment Active" : "Use Local Test Payment"}
            </Button>
          ) : null}
          <Button
            onClick={handleConfirmDelivery}
            disabled={submitting || loadingToken}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 3,
              py: 1.05,
              fontWeight: 900,
              color: "#fff",
              background: "linear-gradient(90deg,#15803d,#166534)",
              "&:hover": {
                background: "linear-gradient(90deg,#166534,#14532d)",
              },
            }}
          >
            {submitting ? "Confirming..." : "Confirm Delivery"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg,#f8fafc 0%,#eef2ff 100%)",
        px: 2,
        py: 4,
      }}
    >
      <Box sx={{ maxWidth: 960, mx: "auto" }}>
        <Paper
          sx={{
            borderRadius: 3,
            border: "1px solid #dbe2ef",
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box sx={{ p: 2.75, background: "linear-gradient(100deg,#1e3a8a,#1d4ed8 58%,#2563eb)" }}>
            <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: 24 }}>{PAGE_TITLE}</Typography>
            {orgName ? (
              <Typography sx={{ color: "rgba(255,255,255,0.92)", mt: 0.5, fontWeight: 700 }}>
                {orgName}
              </Typography>
            ) : null}
            <Typography sx={{ color: "rgba(255,255,255,0.9)", mt: 0.5 }}>
              Confirm your delivery details, review the selection, and complete payment.
            </Typography>
          </Box>

          <Box sx={{ p: 2.75, backgroundColor: "#fff" }}>
            {loading ? (
              <Box sx={{ py: 8, display: "grid", placeItems: "center", gap: 1.1 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Loading delivery scheduling...
                </Typography>
              </Box>
            ) : (
              <>
                {renderSnapshot()}

                {error ? (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {error}
                  </Alert>
                ) : null}

                {success ? (
                  <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                    {success}
                  </Alert>
                ) : null}

                {!exists ? null : isScheduled ? (
                  renderReadOnlyDetails()
                ) : (
                  <Box sx={{ display: "grid", gap: 2 }}>
                    {step === 1 ? renderStepOne() : null}
                    {step === 2 ? renderStepTwo() : null}
                    {step === 3 ? renderStepThree() : null}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
